import type { MessageView } from "@beacon/core/chat"
import { threadOf, threadParam } from "./thread-place.ts"

/*
 * Where an exact message is, as an address any surface can link to (a saved message, a pin, an
 * unread conversation's first unread message, a search result), and how a conversation or thread
 * finds it: the rules behind landing on one, apart from the page so they can be checked without a
 * browser.
 *
 * A message said in the conversation is `/chat/<conversation>?message=<message>`; a reply is in its
 * thread, `/chat/<conversation>?thread=<its thread's message>&message=<reply>`, so the thread opens
 * beside the conversation (or as the page, on a phone) and lands on the reply there. The address
 * keeps it, so a reload lands there again.
 *
 * An unread conversation opens where the Member reads on from (`?from=`, `readingFromHref`), its
 * first unread message: the conversation reads back to it and shows it, but it isn't the address
 * of that message, so nothing marks it as linked to, and the page drops it from the address once
 * it has opened there (`withoutReadingFrom`).
 */

/** The search parameter naming the message an address lands on. */
export const messageParam = "message"

/** The search parameter naming the message a conversation opens reading on from. */
const fromParam = "from"

/** The message `search` lands on, none when it names none. */
export function messageOf(search: URLSearchParams): string | null {
  return search.get(messageParam) || null
}

/** The address that opens a message where it was said: its conversation, or its thread. */
export function messageHref(message: Pick<MessageView, "conversationId" | "id" | "threadRootId">) {
  const search = new URLSearchParams()
  if (message.threadRootId) search.set(threadParam, message.threadRootId)
  search.set(messageParam, message.id)
  return `/chat/${encodeURIComponent(message.conversationId)}?${search.toString()}`
}

/** The address that opens a conversation reading on from message `messageId`, its first unread. */
export function readingFromHref(conversationId: string, messageId: string) {
  const search = new URLSearchParams({ [fromParam]: messageId })
  return `/chat/${encodeURIComponent(conversationId)}?${search.toString()}`
}

/** `search` without where reading started, once the conversation has opened there. */
export function withoutReadingFrom(search: URLSearchParams) {
  const next = new URLSearchParams(search)
  next.delete(fromParam)
  return next
}

/**
 * Which message each part of the page lands on for `search`: the conversation on the message, or,
 * for a reply, on the message its thread started from, and the thread on the reply. An address
 * that names no message lands nowhere, so a thread opened alone opens at its newest reply. One
 * that names where reading starts (`readingFromHref`) lands the conversation there, unmarked:
 * `marked` says the address is a link to the message landed on.
 */
export function landingsOf(search: URLSearchParams): {
  conversation: string | null
  thread: string | null
  marked: boolean
} {
  const message = messageOf(search)
  const thread = threadOf(search)
  if (!message) {
    const from = thread ? null : search.get(fromParam) || null
    return { conversation: from, thread: null, marked: false }
  }
  if (!thread) return { conversation: message, thread: null, marked: true }
  return { conversation: thread, thread: message === thread ? null : message, marked: true }
}

/**
 * How many earlier pages a conversation or thread reads, at most, looking for a message to land on
 * before it gives up and opens at the newest: 20 pages of 50, so 1,000 messages back.
 */
export const maxLandingPages = 20

/**
 * What landing on a message does next: `land` once the timeline holds it, `read-earlier` while it
 * doesn't and there is an earlier page (within `maxLandingPages`), and `missing` once there is
 * none, or the limit is reached: the message is gone, or too far back to find this way.
 */
export function landingStep({
  held,
  hasEarlier,
  pagesRead,
}: {
  held: boolean
  hasEarlier: boolean
  pagesRead: number
}): "land" | "read-earlier" | "missing" {
  if (held) return "land"
  return hasEarlier && pagesRead < maxLandingPages ? "read-earlier" : "missing"
}
